<template>
  <!-- Desktop dropdown -->
  <div v-if="!isMobile" class="w-72 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
    <!-- Header: avatar + greeting -->
    <header class="px-4 py-4 bg-gradient-to-br from-brand-50 to-white border-b border-slate-100">
      <div class="flex items-center gap-3">
        <span class="h-10 w-10 rounded-full bg-brand-500 text-white font-bold flex items-center justify-center text-sm shrink-0">
          {{ initials }}
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{{ $t('userMenu.hello') }}</p>
          <p class="font-semibold text-slate-900 truncate capitalize">{{ client?.name || $t('userMenu.welcomeFallback') }}</p>
        </div>
      </div>
    </header>

    <!-- Menu -->
    <ul class="p-2">
      <li v-for="item in menuItems" :key="item.to">
        <router-link
          :to="item.to"
          class="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
          @click="$emit('close')"
        >
          <span :class="[
            'h-8 w-8 rounded-md flex items-center justify-center shrink-0',
            item.tone,
          ]">
            <component :is="item.icon" class="h-4 w-4" />
          </span>
          <span class="text-sm font-medium text-slate-900 flex-1 truncate">{{ item.label }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
        </router-link>
      </li>
    </ul>

    <!-- Logout -->
    <div class="p-2 border-t border-slate-100">
      <button
        type="button"
        class="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-red-50 transition-colors text-left"
        @click="logout"
      >
        <span class="h-8 w-8 rounded-md bg-red-50 text-red-600 flex items-center justify-center shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
        </span>
        <span class="text-sm font-medium text-red-700 flex-1">{{ $t('userMenu.logout') }}</span>
      </button>
    </div>
  </div>

  <!-- Mobile: full-screen sheet -->
  <div v-else class="fixed inset-0 bg-white z-50 flex flex-col">
    <header class="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
      <div class="flex items-center gap-3 min-w-0">
        <span class="h-10 w-10 rounded-full bg-brand-500 text-white font-bold flex items-center justify-center text-sm shrink-0">
          {{ initials }}
        </span>
        <div class="min-w-0">
          <p class="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{{ $t('userMenu.hello') }}</p>
          <p class="font-semibold text-slate-900 truncate capitalize">{{ client?.name || $t('userMenu.welcomeFallback') }}</p>
        </div>
      </div>
      <button
        class="h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
        :aria-label="$t('common.close')"
        @click="$emit('close')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    </header>

    <ul class="flex-1 overflow-y-auto p-3 space-y-1">
      <li v-for="item in menuItems" :key="`m-${item.to}`">
        <router-link
          :to="item.to"
          class="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors"
          @click="$emit('close')"
        >
          <span :class="[
            'h-10 w-10 rounded-lg flex items-center justify-center shrink-0',
            item.tone,
          ]">
            <component :is="item.icon" class="h-5 w-5" />
          </span>
          <span class="text-base font-medium text-slate-900 flex-1 truncate">{{ item.label }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
        </router-link>
      </li>
    </ul>

    <div class="border-t border-slate-200 p-3">
      <button
        type="button"
        class="w-full h-12 rounded-lg bg-red-50 text-red-700 font-semibold inline-flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
        @click="logout"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
        {{ $t('userMenu.logout') }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, h } from 'vue';
import { mapState } from 'vuex';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);

const Icon = (paths, viewBox = '0 0 24 24') => defineComponent({
  setup() {
    return () => h('svg', {
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 2,
    }, paths.map((d) => h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d })));
  },
});

const HomeIcon     = Icon(['M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3v-7h6v7h3a1 1 0 001-1V10']);
const OrdersIcon   = Icon(['M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2']);
const InvoicesIcon = Icon(['M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-4-2-3 2-3-2-4 2V6a2 2 0 012-2z']);
const ListsIcon    = Icon(['M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z']);
const AddrIcon     = Icon(['M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z', 'M15 11a3 3 0 11-6 0 3 3 0 016 0z']);
const ReturnIcon   = Icon(['M3 10h11a5 5 0 010 10h-2M3 10l4-4M3 10l4 4']);
const SettingsIcon = Icon(['M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', 'M15 12a3 3 0 11-6 0 3 3 0 016 0z']);

export default {
  name: 'DialogComponent',
  emits: ['close'],
  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  computed: {
    ...mapState({ client: (state) => state.client }),
    isMobile() {
      return this.windowWidth < 768;
    },
    initials() {
      const name = (this.client?.name || '').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/);
      const a = parts[0]?.[0] || '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase() || a.toUpperCase();
    },
    menuItems() {
      // Re-resolved on every render so labels re-translate when locale changes.
      return [
        { to: '/account/dashboard', label: t('userMenu.accountOverview'), icon: HomeIcon,     tone: 'bg-brand-50 text-brand-600' },
        { to: '/account/orders',    label: t('userMenu.myOrders'),        icon: OrdersIcon,   tone: 'bg-emerald-50 text-emerald-600' },
        { to: '/account/invoices',  label: t('userMenu.myInvoices'),      icon: InvoicesIcon, tone: 'bg-amber-50 text-amber-600' },
        { to: '/account/lists',     label: t('userMenu.myLists'),         icon: ListsIcon,    tone: 'bg-rose-50 text-rose-600' },
        { to: '/account/addresses', label: t('userMenu.addresses'),       icon: AddrIcon,     tone: 'bg-violet-50 text-violet-600' },
        { to: '/account/returns',   label: t('userMenu.returns'),         icon: ReturnIcon,   tone: 'bg-sky-50 text-sky-600' },
        { to: '/account/profile',   label: t('userMenu.dataPrefs'),       icon: SettingsIcon, tone: 'bg-slate-100 text-slate-600' },
      ];
    },
  },
  methods: {
    handleResize() { this.windowWidth = window.innerWidth; },
    logout() {
      this.$store.dispatch('logout')
        .then(() => {
          this.$emit('close');
          this.$router.push({ name: 'HomePage' });
        })
        .catch((err) => {
          console.error('Error during logout:', err);
        });
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
